import { evaluateSelector } from '@coast/schemas';
import { isNil } from 'lodash';
import {
  useViewTemplate,
  useViewTemplates,
  useWorkflowTemplate,
} from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';

export function useEntityFormViewTemplate({
  workflowTemplateId,
  viewTemplateId,
  type,
}: {
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  type: 'CREATE' | 'EDIT';
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);

  const behaviorSelectors =
    type === 'CREATE'
      ? workflowTemplate?.behavior?.onNewCard?.selectors
      : workflowTemplate?.behavior?.onOpenCard?.selectors;

  const behaviorTemplateId = behaviorSelectors?.find(s => evaluateSelector(s.selector, {}))
    ?.viewTemplateId;

  // Prefer the explicit route slug, then fall back to behavior-resolved ID.
  const resolvedViewTemplateId = viewTemplateId ?? behaviorTemplateId;

  const { viewTemplate: queriedViewTemplate } = useViewTemplate(
    workflowTemplateId,
    resolvedViewTemplateId,
  );

  // onNewCard is currently always null, so behaviorTemplateId will not resolve
  // for the CREATE case — skip this query only when we have a known template ID.
  const { viewTemplates: cardViewTemplates } = useViewTemplates({
    workflowTemplateId,
    type: 'CARD',
    isPublic: false,
    skip: !isNil(resolvedViewTemplateId),
  });

  if (!isNil(resolvedViewTemplateId)) {
    return queriedViewTemplate;
  }

  const onOpenCardSelectors = workflowTemplate?.behavior?.onOpenCard?.selectors;

  // For CREATE: if all but one template is referenced by onOpenCard selectors,
  // the unreferenced one is the create template.
  if (type === 'CREATE') {
    const createTemplate = findCreateTemplateByExclusion(cardViewTemplates, onOpenCardSelectors);
    if (!isNil(createTemplate)) {
      return createTemplate;
    }
  }

  // For EDIT: if all onOpenCard selectors reference the same single template,
  // that is unambiguously the edit/open template regardless of selector conditions.
  if (type === 'EDIT') {
    const editTemplate = findEditTemplateBySingleReference(cardViewTemplates, onOpenCardSelectors);
    if (!isNil(editTemplate)) {
      return editTemplate;
    }
  }

  return legacyFindViewTemplateByName(cardViewTemplates, type);
}

function findCreateTemplateByExclusion(
  cardViewTemplates: API.ViewTemplateFragment[],
  onOpenCardSelectors?: API.NavigateAction['selectors'] | null,
): API.ViewTemplateFragment | undefined {
  if (isNil(onOpenCardSelectors) || cardViewTemplates.length <= 1) {
    return undefined;
  }
  const openCardTemplateIds = new Set(onOpenCardSelectors.map(s => s.viewTemplateId));
  const unreferenced = cardViewTemplates.filter(vt => !openCardTemplateIds.has(vt.id));
  return unreferenced.length === 1 ? unreferenced[0] : undefined;
}

function findEditTemplateBySingleReference(
  cardViewTemplates: API.ViewTemplateFragment[],
  onOpenCardSelectors?: API.NavigateAction['selectors'] | null,
): API.ViewTemplateFragment | undefined {
  if (isNil(onOpenCardSelectors) || onOpenCardSelectors.length === 0) {
    return undefined;
  }
  const uniqueReferencedIds = new Set(onOpenCardSelectors.map(s => s.viewTemplateId));
  if (uniqueReferencedIds.size !== 1) {
    return undefined;
  }
  const [templateId] = uniqueReferencedIds;
  return cardViewTemplates.find(vt => vt.id === templateId);
}

function legacyFindViewTemplateByName(
  cardViewTemplates: API.ViewTemplateFragment[],
  type: 'CREATE' | 'EDIT',
): API.ViewTemplateFragment | undefined {
  // Further exploration here: https://app.shortcut.com/coast/story/46087/no-code-forms-refine-card-view-template-solution
  return (
    cardViewTemplates.find(vt =>
      type === 'CREATE'
        ? vt.name.toLowerCase().startsWith('create ')
        : !vt.name.toLowerCase().startsWith('create '),
    ) ?? cardViewTemplates[0]
  );
}
